import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import classNames from 'classnames';
import { VariableSizeList as List } from 'react-window';
import AutoSizer from 'react-virtualized-auto-sizer';
import { useStore } from 'zustand';
import { ADD_ITEM_ATTR, DraggableTreeNode, PLUS_BUTTON_ATTR, TOGGLE_EXPAND_ATTR, TOGGLE_TARGET_ID_ATTR } from './DraggableTreeNode';
import { DraggableTreeCounter } from './DraggableTreeCounter';
import { NODE_ID_ATTR, useTreeDragDrop } from './useTreeDragDrop';
import { computeInitialExpanded, createTreeStore } from './useTreeStore';
import './DraggableTreeViewStyles.scss';
/** Default row height (px) — matches the previous HierarchicalTree visual spec. */
const DEFAULT_ROW_HEIGHT = 48;
/**
 * Extra height (px) added to a node row that is the last descendant of a
 * subtree before returning to a shallower depth. Produces the visual gap
 * between a parent's children and the next root/sibling group that the
 * original `HierarchicalTree` achieved via natural block flow.
 */
const LAST_IN_GROUP_GAP = 10;
/** Default visualized container height (px) when none is provided. */
const DEFAULT_CONTAINER_HEIGHT = 600;
// ---------------------------------------------------------------------------
// Tree-building helpers (pure — no React state)
// ---------------------------------------------------------------------------
/**
 * Build a nested `TreeNode[]` structure from the incoming (possibly flat or
 * already-nested) `items` array. Depth, `hasChildren`, and sibling metadata
 * are computed during this pass so the result is ready to flatten without
 * further traversal.
 *
 * This build is pure w.r.t. React state — it only depends on the `items`
 * reference and is memoised at the call site.
 */
function buildTreeStructure(items) {
    if (items.length === 0)
        return [];
    // Determine whether the incoming list is already nested (roots carry a
    // `children[]` array) or flat (`parentId` relationships only).
    const topLevelIds = new Set(items.map((i) => i.id));
    const roots = items.filter((item) => {
        const pid = item.parentId;
        if (pid === null || pid === undefined)
            return true;
        return !topLevelIds.has(pid);
    });
    const alreadyNested = roots.some((r) => r.children && r.children.length > 0);
    const convertNested = (item, depth) => {
        const children = item.children ? item.children.map((child) => convertNested(child, depth + 1)) : [];
        return {
            id: item.id,
            parentId: item.parentId,
            position: item.position,
            data: item.data,
            isDragDisabled: item.isDragDisabled,
            hideAddButton: item.hideAddButton,
            addDisabledReason: item.addDisabledReason,
            depth,
            children,
            hasChildren: children.length > 0
        };
    };
    if (alreadyNested) {
        return roots.map((root) => convertNested(root, 0));
    }
    // Flat build: bucket items by parentId.
    const byParent = new Map();
    items.forEach((item) => {
        var _a;
        const pid = (_a = item.parentId) !== null && _a !== void 0 ? _a : null;
        const bucket = byParent.get(pid);
        if (bucket)
            bucket.push(item);
        else
            byParent.set(pid, [item]);
    });
    const build = (parentId, depth) => {
        var _a;
        const bucket = (_a = byParent.get(parentId)) !== null && _a !== void 0 ? _a : [];
        return bucket.map((item) => {
            var _a;
            const children = build(item.id, depth + 1);
            return {
                id: item.id,
                parentId: (_a = item.parentId) !== null && _a !== void 0 ? _a : null,
                position: item.position,
                data: item.data,
                isDragDisabled: item.isDragDisabled,
                hideAddButton: item.hideAddButton,
                addDisabledReason: item.addDisabledReason,
                depth,
                children,
                hasChildren: children.length > 0
            };
        });
    };
    return build(null, 0);
}
/**
 * Walk the nested tree and return the visible entries (roots + descendants of
 * expanded nodes). For every parent that has children we also emit a counter
 * entry — rendered as a dedicated row, this mirrors the "▼ N items" affordance
 * the original `HierarchicalTree` showed inside its parent container. Skipping
 * the subtree of a collapsed node is still how we defer child processing.
 */
function flattenVisible(nodes, expanded) {
    const flat = [];
    const walk = (list, ancestorLastSiblings) => {
        list.forEach((node, index) => {
            const isLastSibling = index === list.length - 1;
            const flatNode = Object.assign(Object.assign({}, node), { isLastSibling, ancestorLastSiblings, isLastInGroup: false });
            flat.push({ kind: 'node', key: `n:${node.id}`, node: flatNode });
            if (node.hasChildren) {
                flat.push({ kind: 'counter', key: `c:${node.id}`, parent: flatNode });
                if (expanded.has(node.id) && node.children && node.children.length > 0) {
                    walk(node.children, [...ancestorLastSiblings, isLastSibling]);
                }
            }
        });
    };
    walk(nodes, []);
    // Post-pass: mark each node whose successor lives at a shallower depth.
    // This is the "subtree ends here" boundary — the SCSS reserves extra air
    // below these rows, matching the original HierarchicalTree group spacing.
    for (let i = 0; i < flat.length - 1; i += 1) {
        const cur = flat[i];
        if (cur.kind !== 'node')
            continue;
        const next = flat[i + 1];
        const nextDepth = next.kind === 'node' ? next.node.depth : next.parent.depth;
        if (nextDepth < cur.node.depth) {
            cur.node.isLastInGroup = true;
        }
    }
    return flat;
}
function Row({ index, style, data }) {
    const entry = data.entries[index];
    if (!entry)
        return null;
    if (entry.kind === 'counter') {
        return (React.createElement("div", { style: style },
            React.createElement(DraggableTreeCounter, { parentNode: entry.parent, store: data.store, pluralKey: data.pluralKey })));
    }
    return (React.createElement("div", { style: style },
        React.createElement(DraggableTreeNode, { node: entry.node, store: data.store, itemRenderer: data.itemRenderer, showPlusButton: data.showPlusButton, dragHandleClassName: data.dragHandleClassName, itemClassName: data.itemClassName, maxDepth: data.maxDepth })));
}
// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------
export function DraggableTreeView({ items, itemRenderer, onDragEnd, maxDepth = 5, expandedByDefault = 2, className, showPlusButton = false, onPlusClick, onAddItem, dragHandleClassName = 'tree-drag-handle', itemClassName = 'tree-item', pluralKey = 'items', rowHeight = DEFAULT_ROW_HEIGHT, height = DEFAULT_CONTAINER_HEIGHT }) {
    // One store per component instance — supports multiple trees on the same page.
    const [store] = useState(() => createTreeStore());
    // Pure tree structure — only rebuilt when `items` changes.
    const treeStructure = useMemo(() => buildTreeStructure(items), [items]);
    // Subscribe to the expanded-items slice to re-flatten when expand state changes.
    const expandedItems = useStore(store, (state) => state.expandedItems);
    const flattenedEntries = useMemo(() => flattenVisible(treeStructure, expandedItems), [treeStructure, expandedItems]);
    // Node-only view used by click delegation to resolve `onAddItem`/`onPlusClick`
    // against the real node; counter entries are filtered out here.
    const flattenedNodes = useMemo(() => flattenedEntries.filter((e) => e.kind === 'node').map((e) => e.node), [flattenedEntries]);
    // One-time initial expansion. Runs when `items` first arrives — subsequent
    // changes don't re-expand (that would fight user-driven collapse).
    const didInitExpansion = useRef(false);
    useEffect(() => {
        if (didInitExpansion.current || items.length === 0)
            return;
        const initial = computeInitialExpanded(items, expandedByDefault);
        if (initial.size > 0) {
            store.getState().setExpandedItems(initial);
        }
        didInitExpansion.current = true;
    }, [items, expandedByDefault, store]);
    // Drag-and-drop container handlers (event delegation).
    const { handleContainerDragStart, handleContainerDragOver, handleContainerDragEnd, handleContainerDrop, handleContainerDragLeave } = useTreeDragDrop({
        items,
        maxDepth,
        onDragEnd,
        store
    });
    // Click delegation — expand toggle + plus/add-item buttons.
    const handleContainerClick = useCallback((e) => {
        var _a, _b;
        if (!(e.target instanceof Element))
            return;
        const toggle = e.target.closest(`[${TOGGLE_EXPAND_ATTR}]`);
        if (toggle) {
            // Counter rows carry the id explicitly on the toggle button so the
            // handler never has to walk into the (counter-less) `data-node-id`
            // zone. Fall back to the ancestor lookup for legacy callers that
            // place `data-toggle-expand` inside a node row.
            const explicit = toggle.getAttribute(TOGGLE_TARGET_ID_ATTR);
            const rawId = (_b = explicit !== null && explicit !== void 0 ? explicit : (_a = toggle.closest(`[${NODE_ID_ATTR}]`)) === null || _a === void 0 ? void 0 : _a.getAttribute(NODE_ID_ATTR)) !== null && _b !== void 0 ? _b : null;
            if (rawId !== null && rawId !== undefined && rawId !== '') {
                const asNumber = Number(rawId);
                const nodeId = Number.isFinite(asNumber) && String(asNumber) === rawId ? asNumber : rawId;
                store.getState().toggleExpand(nodeId);
            }
            return;
        }
        const addBtn = e.target.closest(`[${ADD_ITEM_ATTR}]`);
        if (addBtn && onAddItem) {
            const nodeEl = addBtn.closest(`[${NODE_ID_ATTR}]`);
            const rawId = nodeEl === null || nodeEl === void 0 ? void 0 : nodeEl.getAttribute(NODE_ID_ATTR);
            if (rawId !== null && rawId !== undefined && rawId !== '') {
                const asNumber = Number(rawId);
                const nodeId = Number.isFinite(asNumber) && String(asNumber) === rawId ? asNumber : rawId;
                const found = flattenedNodes.find((n) => n.id === nodeId);
                if (found)
                    onAddItem(found);
            }
            return;
        }
        const plusBtn = e.target.closest(`[${PLUS_BUTTON_ATTR}]`);
        if (plusBtn && onPlusClick) {
            const nodeEl = plusBtn.closest(`[${NODE_ID_ATTR}]`);
            const rawId = nodeEl === null || nodeEl === void 0 ? void 0 : nodeEl.getAttribute(NODE_ID_ATTR);
            if (rawId !== null && rawId !== undefined && rawId !== '') {
                const asNumber = Number(rawId);
                const nodeId = Number.isFinite(asNumber) && String(asNumber) === rawId ? asNumber : rawId;
                const found = flattenedNodes.find((n) => n.id === nodeId);
                if (found)
                    onPlusClick(found);
            }
        }
    }, [store, onAddItem, onPlusClick, flattenedNodes]);
    const rowData = useMemo(() => ({
        entries: flattenedEntries,
        store,
        itemRenderer,
        showPlusButton,
        dragHandleClassName,
        itemClassName,
        maxDepth,
        pluralKey
    }), [flattenedEntries, store, itemRenderer, showPlusButton, dragHandleClassName, itemClassName, maxDepth, pluralKey]);
    // Per-row sizing function for `VariableSizeList`. Last-in-group nodes get
    // an additional gap baked into their row height so the next root/ancestor
    // visually breathes. Kept stable across renders via the closure over
    // `flattenedEntries`; the `resetAfterIndex` effect below invalidates the
    // list's internal offset cache whenever entries change.
    const getItemSize = useCallback((index) => {
        const entry = flattenedEntries[index];
        if (!entry)
            return rowHeight;
        if (entry.kind === 'node' && entry.node.isLastInGroup)
            return rowHeight + LAST_IN_GROUP_GAP;
        return rowHeight;
    }, [flattenedEntries, rowHeight]);
    // `VariableSizeList` memoises computed offsets — reset whenever the
    // flattened entries change so the new heights are picked up.
    const listRef = useRef(null);
    useEffect(() => {
        var _a;
        (_a = listRef.current) === null || _a === void 0 ? void 0 : _a.resetAfterIndex(0, true);
    }, [flattenedEntries]);
    const containerClassName = classNames('draggable-tree-view', 'hierarchical-tree', className);
    // Use AutoSizer when the consumer passes a non-numeric (string) height so
    // the virtualized list adapts to its parent. Numeric heights go directly.
    const useAutoSizer = typeof height !== 'number';
    return (React.createElement("div", { className: containerClassName, role: "tree", onDragStart: handleContainerDragStart, onDragOver: handleContainerDragOver, onDragEnd: handleContainerDragEnd, onDrop: handleContainerDrop, onDragLeave: handleContainerDragLeave, onClick: handleContainerClick, onKeyDown: () => undefined, tabIndex: -1, style: typeof height === 'string' ? { height } : undefined }, useAutoSizer ? (React.createElement(AutoSizer, { disableWidth: true }, ({ height: autoH }) => (
    // @ts-expect-error react-window class types incompatible with @types/react@18
    React.createElement(List, { ref: listRef, className: "draggable-tree-view__list", height: autoH, itemCount: flattenedEntries.length, itemSize: getItemSize, estimatedItemSize: rowHeight, width: "100%", itemData: rowData, itemKey: (index, data) => { var _a, _b; return (_b = (_a = data.entries[index]) === null || _a === void 0 ? void 0 : _a.key) !== null && _b !== void 0 ? _b : index; } }, Row)))) : (
    // @ts-expect-error react-window class types incompatible with @types/react@18
    React.createElement(List, { ref: listRef, className: "draggable-tree-view__list", height: height, itemCount: flattenedEntries.length, itemSize: getItemSize, estimatedItemSize: rowHeight, width: "100%", itemData: rowData, itemKey: (index, data) => { var _a, _b; return (_b = (_a = data.entries[index]) === null || _a === void 0 ? void 0 : _a.key) !== null && _b !== void 0 ? _b : index; } }, Row))));
}
